import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {useState, useEffect} from 'react';

import DocumentStore from 'notebook/documents/DocumentStore';
import {MethodUpdater} from 'procedures/design/LegacyProcedureEditor';
import DilutionsTable from 'procedures/design/method-config/DilutionsTable';
import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedureValidationErrorType} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  filterProcedureValidationErrors,
  findTestsTabValidationErrors,
} from 'procedures/design/validation/procedureValidationUtils';
import {Method, MethodNode} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  ProcedurePageContext,
} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import TestDefinitionsTableAdapter from '../../tables/TestDefinitionsTableAdapter';
import DilutionAssignmentSelector from './DilutionAssignmentSelector';
import {MethodEditorTabSavingButtons, styles as sharedStyles} from './MethodEditorTabShared';
import TestDefinitionsTable from './TestDefinitionsTable';

// Ensure saveImmediately calls throw all errors (including 400s) so we can handle them uniquely
const SHOULD_SAVE_IMMEDIATELY_BUBBLE_UP_ERRORS = true;
export interface TestTablesEditorProps {
  isEditingMethodTab: boolean;
  isProcedureReadOnly: boolean;
  editingMethodCid: string;
  selectedMethod: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  updateIsEditingState: (newState: boolean) => void;
}

// TODO(BNCH-105827): Refactor so there's less code repetition between this and the MethodDefinitionTables
function stripEmptyRows(method: MethodNode): MethodNode {
  return produce(method, (draftMethod) => {
    draftMethod.testDefinitions = draftMethod.testDefinitions.filter(
      (td) => !TestDefinitionsTableAdapter.isTestDefinitionEmpty(td)
    );
  });
}

export default function TestTablesEditor({
  isEditingMethodTab,
  isProcedureReadOnly,
  editingMethodCid,
  selectedMethod,
  onChangeMethod,
  updateIsEditingState,
}: TestTablesEditorProps): JSX.Element {
  const {
    legacySaveImmediately,
    resetDraftFlowchartChanges,
    registerLegacyPreSaveImmediatelyMiddleware,
    unregisterLegacyPreSaveImmediatelyMiddleware,
    procedureType,
  } = ProcedurePageContext.useContext();
  const [middlewareId, setMiddlewareId] = useState<string | null>(null);
  const [isTableReadyForEditing, setIsTableReadyForEditing] = useState(false);

  const {procedureValidationErrors, setAnyTabValidationErrors} = ProcedureValidationContext.useContext();
  const relevantProcedureValidationErrors = findTestsTabValidationErrors(
    procedureValidationErrors,
    editingMethodCid
  );
  const productLimitErrors = filterProcedureValidationErrors(procedureValidationErrors, {
    errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
  });

  const shouldBlockEditingDefinitions = !isEditingMethodTab || isProcedureReadOnly;

  const anyErrors = relevantProcedureValidationErrors.length > 0 || productLimitErrors.length > 0;
  const errorText =
    productLimitErrors.length > 0
      ? `Test definitions table can't be submitted because the number of tests in your ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} exceeds ${PROCEDURE_PRODUCT_LIMITS.TEST_DEFINITIONS_PER_PROCEDURE}`
      : 'Please fix errors in the tables below';

  // On unmount, we ensure that the error status is reset, and any middleware is removed
  useEffect(() => {
    setAnyTabValidationErrors(false);
    if (middlewareId != null) {
      unregisterLegacyPreSaveImmediatelyMiddleware(middlewareId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register the middleware for removing empty rows in test definition table
  useEffect(() => {
    if (middlewareId != null) {
      unregisterLegacyPreSaveImmediatelyMiddleware(middlewareId);
    }
    if (isEditingMethodTab) {
      const newMiddlewareId = registerLegacyPreSaveImmediatelyMiddleware(selectedMethod.cid, stripEmptyRows);
      setMiddlewareId(newMiddlewareId);
    } else {
      setMiddlewareId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingMethodTab, selectedMethod.cid]);

  return (
    <div
      className={classNames(css(styles.testsWrapper), css(sharedStyles.detailsContainer))}
      {...getTestProps({component: 'TestTablesEditor', element: 'root'})}
    >
      <div className={css(sharedStyles.detailsHeader)}>
        <div>
          <FieldLabel label="Test definitions" />
          <p>Test definitions specify the type of results expected, including for multiplex assays.</p>
        </div>
        {!isProcedureReadOnly && (
          <MethodEditorTabSavingButtons
            isEditingMethodTab={isEditingMethodTab}
            anyErrors={anyErrors}
            errorText={errorText}
            areTablesStillLoading={!isTableReadyForEditing}
            handleOnCancel={() => {
              resetDraftFlowchartChanges();
              updateIsEditingState(false);
            }}
            handleOnSubmit={async () => {
              try {
                // This call requires an immediate API call, with an overlay. The middleware (above) handles cleanup tasks
                await legacySaveImmediately(
                  () => ({
                    blockingSaveObjectSource: BlockingSaveObjectSource.TestDefinition,
                    blockingSaveOperation: BlockingSaveOperation.Update,
                  }),
                  'Saving test definitions...',
                  SHOULD_SAVE_IMMEDIATELY_BUBBLE_UP_ERRORS
                );
                // When saving test definitions, we need to clear the saved documents in memory so
                // that the user fetches them with any new changes applied
                DocumentStore.resetState();
                updateIsEditingState(false);
              } catch (e) {
                if (e.status === 400) {
                  // Swallow 400 errors to prevent duplicate toasts
                  // Reset the draft changes if there are validation errors
                  resetDraftFlowchartChanges();
                  updateIsEditingState(false);
                } else {
                  throw e;
                }
              }
            }}
            handleOnEdit={() => {
              updateIsEditingState(true);
            }}
          />
        )}
      </div>
      <div className={css(sharedStyles.detailsContent)}>
        <div className={css(sharedStyles.detailsContentScrollContainer)}>
          <TestDefinitionsTable
            method={selectedMethod}
            onChangeMethod={onChangeMethod}
            onReadyForEditing={() => {
              setIsTableReadyForEditing(true);
            }}
            locked={shouldBlockEditingDefinitions}
          />
          <DilutionsTable
            method={selectedMethod}
            onChangeMethod={onChangeMethod}
            locked={shouldBlockEditingDefinitions}
          />
          <DilutionAssignmentSelector
            method={selectedMethod}
            onChangeMethod={onChangeMethod}
            locked={shouldBlockEditingDefinitions}
          />
        </div>
      </div>
    </div>
  );
}

export const styles = StyleSheet.create({
  testsWrapper: {
    marginTop: '4px',
    marginLeft: '4px',
  },
});
